import { useCallback, useEffect, useMemo, useState } from 'react';
import { BULK_FLEET_LIMIT } from '@galaxis/shared';
import { useGameStore } from '../store/game-store.ts';
import { FleetList, type FleetListState } from './fleets/FleetList.tsx';
import { FleetDetail } from './fleets/FleetDetail.tsx';
import { FleetOverview } from './fleets/FleetOverview.tsx';
import { FleetShipyard } from './fleets/FleetShipyard.tsx';
import { FleetBulkBar } from './fleets/FleetBulkBar.tsx';
import { GroupDialog, useFleetGroups } from './fleets/FleetGroups.tsx';
import { toRows, isActiveFleet } from './fleets/fleet-category.ts';
import { pruneSelection, toggleAllVisible, toggleId } from './fleets/fleet-overview.ts';

/**
 * Fleet command center: overview + list + details side by side from md up; on phones one at a time
 * with a clear way back. Filters survive opening a fleet. Multi-select mode turns the list into a
 * checklist with a bulk action bar. No per-second state here — only countdowns tick.
 */
export function FleetsView() {
  const { fleets, selectedFleetId, selectFleet, galaxyMap } = useGameStore();
  const [listState, setListState] = useState<FleetListState>({ category: 'all', status: 'any', query: '', sort: 'activity', group: 'any' });
  const { groups, setGroups } = useFleetGroups(fleets);
  const [selectMode, setSelectMode] = useState(false);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const [manageGroups, setManageGroups] = useState(false);
  // Shipyard panel: replaces the detail column (on phones the whole view); pre-set from a fleet's shortcut
  const [yard, setYard] = useState<{ systemId: string | null; fleetId: string | null } | null>(null);

  const names = useMemo(() => new Map((galaxyMap?.systems ?? []).map(s => [s.id, s.name])), [galaxyMap]);
  const systemName = useCallback((id: string | null) => (id ? names.get(id) : undefined) ?? '—', [names]);
  const rows = useMemo(() => toRows(fleets, id => systemName(id)), [fleets, systemName]);

  const selected = fleets.find(f => f.id === selectedFleetId && isActiveFleet(f)) ?? null;

  // A merged/destroyed fleet disappears from the list: drop the stale selection
  useEffect(() => {
    if (selectedFleetId && !selected && fleets.length > 0) selectFleet(null);
  }, [selectedFleetId, selected, fleets.length, selectFleet]);
  useEffect(() => { setSelection(prev => pruneSelection(prev, fleets)); }, [fleets]);
  // A group that no longer exists cannot stay as the filter
  useEffect(() => {
    if (listState.group !== 'any' && listState.group !== 'none' && groups.length >= 0 && !groups.some(g => g.id === listState.group)) {
      setListState(s => (s.group === listState.group ? { ...s, group: 'any' } : s));
    }
  }, [groups, listState.group]);

  const selectedFleets = useMemo(() => fleets.filter(f => selection.has(f.id)), [fleets, selection]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selection.has(id));
  const showList = (!selected || selectMode) && !yard;

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 md:p-6" data-testid="fleets-view">
      <div className="max-w-6xl mx-auto md:grid md:grid-cols-[minmax(300px,380px)_1fr] md:gap-6">
        <div className={showList ? 'block min-w-0' : 'hidden md:block min-w-0'}>
          <div className="space-y-3">
            <button type="button" data-testid="open-shipyard" onClick={() => { setSelectMode(false); setYard({ systemId: null, fleetId: null }); }}
              aria-pressed={!!yard} className="w-full rounded-xl border border-violet-400/50 text-violet-200 text-sm font-medium"
              style={{ minHeight: 48, background: yard ? 'rgba(167,139,250,0.2)' : 'rgba(167,139,250,0.08)' }}>
              🏭 Hajógyártás
            </button>
            <FleetOverview fleets={fleets} systemName={systemName} onSelect={id => { setSelectMode(false); setYard(null); selectFleet(id); }} />
            <FleetList
              rows={rows}
              state={listState}
              onState={setListState}
              selectedId={selectedFleetId}
              onSelect={id => { setYard(null); selectFleet(id); }}
              systemName={systemName}
              groups={groups}
              onManageGroups={() => setManageGroups(true)}
              selectMode={selectMode}
              selection={selection}
              onToggleSelect={id => setSelection(prev => (prev.has(id) || prev.size < BULK_FLEET_LIMIT ? toggleId(prev, id) : prev))}
              onSelectMode={on => { setSelectMode(on); if (!on) setSelection(new Set()); }}
              onVisible={setVisibleIds}
            />
          </div>
        </div>
        <div className={(selected && !selectMode) || yard ? 'block min-w-0' : 'hidden md:block min-w-0'}>
          {yard ? (
            <FleetShipyard
              key={`${yard.systemId ?? ''}:${yard.fleetId ?? ''}`}
              fleets={fleets}
              initialSystemId={yard.systemId}
              initialFleetId={yard.fleetId}
              onClose={() => setYard(null)}
              onSelectFleet={id => { setYard(null); selectFleet(id); }}
            />
          ) : selected ? (
            <FleetDetail
              key={selected.id}
              fleet={selected}
              fleets={fleets}
              groups={groups}
              onGroups={setGroups}
              onBack={() => selectFleet(null)}
              onSelectFleet={id => selectFleet(id)}
              onBuildShips={() => setYard({ systemId: selected.currentSystemId, fleetId: selected.id })}
            />
          ) : (
            <div className="sci-fi-panel rounded-xl p-8 text-center text-sm text-slate-500 md:mt-14">
              {selectMode ? 'Jelölj ki flottákat a listában, majd adj nekik közös parancsot.' : 'Válassz egy flottát a részletekhez.'}
            </div>
          )}
        </div>
      </div>

      {selectMode && (
        <FleetBulkBar
          selected={selectedFleets}
          groups={groups}
          onGroups={setGroups}
          map={galaxyMap}
          allSelected={allVisibleSelected}
          onAll={() => setSelection(prev => toggleAllVisible(prev, visibleIds, BULK_FLEET_LIMIT))}
          onClear={() => { setSelectMode(false); setSelection(new Set()); }}
        />
      )}

      {manageGroups && <GroupDialog mode="manage" groups={groups} onGroups={setGroups} fleetIds={[]} onClose={() => setManageGroups(false)} />}
    </div>
  );
}
